import { z } from "zod";

export * from "./catalog";

export const EXPERIMENT_STATUS = ["DRAFT", "RUNNING", "CONCLUDED"] as const;
export const ExperimentStatusSchema = z.enum(EXPERIMENT_STATUS);
export type ExperimentStatusDto = z.infer<typeof ExperimentStatusSchema>;

/** `^[a-z0-9_]+$` keeps keys usable as Mixpanel property names without escaping. */
export const experimentKeyRegex = /^[a-z0-9_]+$/;

export const ExperimentVariantDtoSchema = z.object({
  key: z.string().min(1).max(64).regex(experimentKeyRegex),
  /** Relative share; weights need not sum to 100. */
  weight: z.number().int().nonnegative(),
});
export type ExperimentVariantDto = z.infer<typeof ExperimentVariantDtoSchema>;

const VariantsSchema = z
  .array(ExperimentVariantDtoSchema)
  .min(1)
  .superRefine((variants, ctx) => {
    if (new Set(variants.map((variant) => variant.key)).size !== variants.length) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Variant keys must be unique" });
    }
    if (variants.reduce((sum, variant) => sum + variant.weight, 0) <= 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: "Total variant weight must be positive" });
    }
  });

export const ExperimentDtoSchema = z.object({
  key: z.string().min(1).max(64).regex(experimentKeyRegex),
  name: z.string().min(1).max(200),
  status: ExperimentStatusSchema,
  variants: VariantsSchema,
  /** Null = every user eligible; else enrol only users created at or after this. */
  enrolFrom: z.coerce.date().nullable(),
  /**
   * Set to end the test: every user reads this arm and enrolment stops.
   * Assignments are untouched — they remain the record of what was assigned.
   */
  // Optional, not just nullable: an end-of-test field that is absent on every
  // normal save already means "not rolled out".
  rolloutArm: z.string().min(1).max(64).nullish(),
  /** Live assignment counts per variant key. Read-only. */
  assignmentCounts: z.record(z.string(), z.number()),
  /** Admin-pinned users. Read-only; managed through the override endpoints. */
  overrides: z.array(z.object({ hubUserId: z.number().int(), variant: z.string() })).default([]),
});
export type ExperimentDto = z.infer<typeof ExperimentDtoSchema>;

export const ExperimentListResponseSchema = z.object({ experiments: ExperimentDtoSchema.array() });
export type ExperimentListResponse = z.infer<typeof ExperimentListResponseSchema>;

export const UpsertExperimentBodySchema = ExperimentDtoSchema.omit({
  key: true,
  assignmentCounts: true,
  overrides: true,
});

/** Pins one user to one arm for end-to-end QA. Solo resolves the identifier first. */
export const SetExperimentOverrideBodySchema = z.object({
  hubUserId: z.number().int().positive(),
  variant: z.string().min(1).max(64).regex(experimentKeyRegex),
});
export type SetExperimentOverrideBody = z.infer<typeof SetExperimentOverrideBodySchema>;
export type UpsertExperimentBody = z.infer<typeof UpsertExperimentBodySchema>;
